// Values as text and the display format (spec/dashboard.md section 6.5): the label of a document everywhere — grid, picker
// cards, the Changes panel, references, the editor header — rendered from the template's format or the auto format; when
// that says nothing but the id, labelText falls back per document (a string, a referenced label, a price, a reward).
import type { EnumDefinition, Json, Parameter, Template } from '../api/types';
import { formatDuration, formatInstant, type DisplayZone } from '../ui/time';
import { isTempId } from '../workspace/Workspace';

export interface DocumentLike {
  id: string;
  unnyId?: string | null;
  template: string;
  data: unknown;
}

/** What the formatters may look up; every resolver is optional and synchronous (pages feed them from their caches). */
export interface FormatContext {
  enums: Map<string, EnumDefinition>;
  templates?: Map<string, Template>;
  zone?: DisplayZone;
  offsets?: { dev?: number; stage?: number };
  /** a document by id or temp id: loaded rows, drafts, or the resolver cache */
  resolve?: (id: string) => DocumentLike | undefined;
  /** a localization string's default-language text by id */
  stringText?: (id: string) => string | undefined;
  /** an asset's name by id */
  assetName?: (id: string) => string | undefined;
  /** a product's label by product id */
  productLabel?: (productId: string) => string | undefined;
  depth?: number;
}

const MAX_DEPTH = 3;

export function shortId(doc: Pick<DocumentLike, 'id' | 'unnyId'>): string {
  return doc.unnyId ?? (isTempId(doc.id) ? 'new' : doc.id.slice(0, 8));
}

/** One parameter value as text: cells, copy, the display format's `{Param}` tokens. */
export function formatValue(p: Parameter, value: Json | undefined, ctx: FormatContext): string {
  if (value === null || value === undefined) return '—';
  switch (p.type) {
    case 'date_time':
      return typeof value === 'number' ? formatInstant(value, ctx.zone ?? 'utc', { offsets: ctx.offsets }) : String(value);
    case 'duration':
      return typeof value === 'number' ? formatDuration(value, zeroWord(p)) : String(value);
    case 'enum': {
      const e = p.enum ? ctx.enums.get(p.enum) : undefined;
      if (!e) return String(value);
      const n = Number(value);
      if (e.isFlags) {
        const names = e.values.filter((v) => Number(v.value) !== 0 && (n & Number(v.value)) === Number(v.value)).map((v) => v.displayName ?? v.name);
        return names.length ? names.join(' | ') : n === 0 ? 'none' : String(n);
      }
      const hit = e.values.find((v) => Number(v.value) === n);
      return hit ? (hit.displayName ?? hit.name) : String(value);
    }
    case 'bool':
      return value ? 'yes' : 'no';
    case 'document': {
      if (typeof value !== 'string') return JSON.stringify(value);
      const target = ctx.resolve?.(value);
      if (!target) return isTempId(value) ? 'new document' : value.slice(0, 8) + '…';
      return (ctx.depth ?? 0) >= MAX_DEPTH ? shortId(target) : labelText(target, { ...ctx, depth: (ctx.depth ?? 0) + 1 });
    }
    case 'localized_string':
      return typeof value === 'string' ? (ctx.stringText?.(value) ?? value.slice(0, 8) + '…') : JSON.stringify(value);
    case 'asset':
      return typeof value === 'string' ? (ctx.assetName?.(value) ?? value.slice(0, 8) + '…') : JSON.stringify(value);
    case 'product': {
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const v = value as { productId?: string; amount?: number | string; currency?: string };
        const own = v.productId ? ctx.productLabel?.(v.productId) : undefined;
        return own ?? `${v.productId ?? '?'} · ${v.amount ?? '?'} ${v.currency ?? ''}`.trim();
      }
      return JSON.stringify(value);
    }
    case 'list': {
      if (!Array.isArray(value)) return String(value);
      if (value.length === 0) return 'empty';
      if (p.of && (p.of.type === 'document' || p.of.type === 'localized_string' || p.of.type === 'enum' || p.of.type === 'string' || p.of.type === 'int' || p.of.type === 'long' || p.of.type === 'float')) {
        const shown = value.slice(0, 3).map((v) => formatValue(p.of!, v as Json, ctx));
        return value.length > 3 ? `${shown.join(', ')} +${value.length - 3}` : shown.join(', ');
      }
      return `${value.length} item${value.length === 1 ? '' : 's'}`;
    }
    case 'object': {
      if (typeof value !== 'object' || value === null || Array.isArray(value)) return JSON.stringify(value);
      if (p.template === 'SmartObjects.Price') {
        const price = value as { type?: number; product?: { productId?: string } | null; items?: Json[]; ads?: number };
        const type = Number(price.type ?? 0);
        if (type === 0) return price.product?.productId ? (ctx.productLabel?.(price.product.productId) ?? price.product.productId) : 'Free';
        if (type === 1) return (price.items ?? []).map((i) => itemAmountText(i, ctx)).join(', ') || 'Soft: nothing';
        return `${String(price.ads ?? 0)} ad${Number(price.ads ?? 0) === 1 ? '' : 's'}`;
      }
      if (p.template === 'SmartObjects.Reward') {
        const items = (value as { items?: Json[] }).items ?? [];
        return items.length ? items.map((i) => itemAmountText(i, ctx)).join(', ') : 'nothing';
      }
      const inline = p.template ? ctx.templates?.get(p.template) : undefined;
      if (inline && (ctx.depth ?? 0) < MAX_DEPTH) {
        return labelText({ id: '', template: inline.name, data: value }, { ...ctx, depth: (ctx.depth ?? 0) + 1 }, inline);
      }
      return JSON.stringify(value).slice(0, 60);
    }
    case 'any':
    case 'profile_data':
      return JSON.stringify(value).slice(0, 60);
    default:
      return String(value);
  }
}

/** `Coins × 500` for an ItemWithAmount object. */
export function itemAmountText(value: Json, ctx: FormatContext): string {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return String(value);
  const v = value as { item?: string | null; count?: number };
  const target = typeof v.item === 'string' ? ctx.resolve?.(v.item) : undefined;
  const name = target ? labelText(target, { ...ctx, depth: (ctx.depth ?? 0) + 1 }) : v.item ? String(v.item).slice(0, 8) + '…' : '(no item)';
  return `${name} × ${String(v.count ?? 0)}`;
}

export function zeroWord(p: Pick<Parameter, 'name'>): string | undefined {
  return /cooldown/i.test(p.name) ? 'no cooldown' : /duration|period|repeat|delay|interval|timeout/i.test(p.name) ? 'none' : undefined;
}

// ---- the display format ------------------------------------------------------------------------------------------------

export type FormatToken =
  | { kind: 'text'; text: string }
  | { kind: 'templateName' }
  | { kind: 'id' }
  | { kind: 'param'; name: string }
  | { kind: 'icon'; shape: string; text: string; color: string }
  | { kind: 'br' };

const TOKEN = /\{([^{}]*)\}/g;

/** `{template.displayName}: {PString} #{PInt} {icon(circle, &&, #666666)} {\n}` into tokens; unknown tokens stay text. */
export function parseFormat(format: string): FormatToken[] {
  const out: FormatToken[] = [];
  let last = 0;
  for (const m of format.matchAll(TOKEN)) {
    const at = m.index ?? 0;
    if (at > last) out.push({ kind: 'text', text: format.slice(last, at) });
    last = at + m[0].length;
    const inner = (m[1] ?? '').trim();
    if (inner === 'template.displayName' || inner === 'template.name') out.push({ kind: 'templateName' });
    else if (inner === 'entity.id' || inner === 'id') out.push({ kind: 'id' });
    else if (inner === '\\n' || inner === 'n' || inner === 'br') out.push({ kind: 'br' });
    else if (inner.startsWith('icon(') && inner.endsWith(')')) {
      const args = inner.slice(5, -1).split(',').map((a) => a.trim());
      out.push({ kind: 'icon', shape: args[0] ?? 'circle', text: args[1] ?? '', color: args[2] ?? '#888888' });
    } else if (/^[A-Za-z_][A-Za-z0-9_]*$/.test(inner)) out.push({ kind: 'param', name: inner });
    else out.push({ kind: 'text', text: m[0] });
  }
  if (last < format.length) out.push({ kind: 'text', text: format.slice(last) });
  return out;
}

/** The format a template without its own uses: the `usedInName` parameters, else the first string / localized string, else the template name and the id. */
export function autoFormat(template: Pick<Template, 'effectiveParameters' | 'flags'>): string {
  const params = template.effectiveParameters;
  const named = params.filter((p) => p.usedInName);
  const hideId = template.flags.includes('hide_id');
  if (named.length > 0) return named.map((p) => `{${p.name}}`).join(' ') + (hideId ? '' : ' {entity.id}');
  const text = params.find((p) => p.type === 'string' || p.type === 'localized_string');
  if (text) return `{${text.name}}` + (hideId ? '' : ' {entity.id}');
  return '{template.displayName} {entity.id}';
}

/** The parameter a format token names, by name or by display name (case-insensitive), own and inherited. */
function paramNamed(template: Pick<Template, 'effectiveParameters'>, name: string): Parameter | undefined {
  const lower = name.toLowerCase();
  return template.effectiveParameters.find((p) => p.name === name) ?? template.effectiveParameters.find((p) => p.name.toLowerCase() === lower || (p.displayName ?? '').toLowerCase() === lower);
}

export interface LabelPart {
  kind: 'text' | 'icon' | 'br';
  text: string;
  shape?: string;
  color?: string;
  /** the `{entity.id}` part: `labelText` ignores it when deciding whether the format said anything */
  fromId?: true;
  /** a localized string whose text did not resolve (rendered as its short id): not meaningful either */
  unresolved?: true;
}

function formatOf(t: Template): string {
  return t.displayFormat && t.displayFormat.trim().length > 0 ? t.displayFormat : autoFormat(t);
}

/** The label as parts (text, icons, breaks) for the node renderer. */
export function labelParts(doc: DocumentLike, ctx: FormatContext, template?: Template): LabelPart[] {
  const t = template ?? ctx.templates?.get(doc.template);
  const data = (doc.data ?? {}) as Record<string, Json>;
  if (!t) return [{ kind: 'text', text: `${doc.template.split('.').pop()} ${shortId(doc)}` }];
  const parts: LabelPart[] = [];
  for (const token of parseFormat(formatOf(t))) {
    switch (token.kind) {
      case 'text':
        parts.push({ kind: 'text', text: token.text });
        break;
      case 'templateName':
        parts.push({ kind: 'text', text: t.displayName ?? t.name.split('.').pop() ?? t.name });
        break;
      case 'id':
        if (doc.id !== '') parts.push({ kind: 'text', text: `[${shortId(doc)}]`, fromId: true });
        break;
      case 'br':
        parts.push({ kind: 'br', text: ' ' });
        break;
      case 'icon':
        parts.push({ kind: 'icon', text: token.text, shape: token.shape, color: token.color });
        break;
      case 'param': {
        const p = paramNamed(t, token.name);
        if (!p) {
          parts.push({ kind: 'text', text: `{${token.name}}` });
          break;
        }
        const value = data[p.name];
        const text = value === null || value === undefined || value === '' ? '' : formatValue(p, value, ctx);
        const unresolved = p.type === 'localized_string' && typeof value === 'string' && value !== '' && ctx.stringText?.(value) === undefined;
        parts.push(unresolved ? { kind: 'text', text, unresolved: true } : { kind: 'text', text });
        break;
      }
    }
  }
  return parts;
}

function joinParts(parts: LabelPart[]): string {
  return parts
    .map((p) => p.text)
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Texts that say nothing about the document: the empty price / reward words of `formatValue`. */
const EMPTY_WORDS = new Set(['', 'Free', 'nothing', '—', 'Soft: nothing']);

interface LabelInfo {
  /** the label as shown */
  text: string;
  /** the label without the id suffix (what a referring document borrows) */
  core: string;
  /** false when the label is only the template name / the id */
  meaningful: boolean;
}

/**
 * The label and whether it names the document. When the format renders nothing but the id (or unresolved string ids), the
 * per-document fallback chain (spec/dashboard.md section 6.5): a non-empty localized string / string parameter, a referenced
 * document's meaningful label, a Price naming a product or listing items, a Reward with items; the id is appended when the
 * format would have shown it. Else `{templateShort} {shortId}`.
 */
function labelInfo(doc: DocumentLike, ctx: FormatContext, template?: Template): LabelInfo {
  const t = template ?? ctx.templates?.get(doc.template);
  const plain = `${doc.template.split('.').pop()} ${shortId(doc)}`;
  if (!t) return { text: plain, core: plain, meaningful: false };
  const parts = labelParts(doc, ctx, t);
  const core = joinParts(parts.filter((p) => !p.fromId && !p.unresolved));
  if (core.length > 0) return { text: joinParts(parts), core: joinParts(parts.filter((p) => !p.fromId)), meaningful: true };
  const fallback = fallbackText(doc, ctx, t);
  if (fallback) {
    const idPart = parts.find((p) => p.fromId);
    return { text: idPart ? `${fallback} ${idPart.text}` : fallback, core: fallback, meaningful: true };
  }
  return { text: plain, core: plain, meaningful: false };
}

/** A string value that is data rather than a name: a colour, a URL, a uuid, a bare number, or too long to be a title. */
function looksLikeData(v: string): boolean {
  return v.length > 80 || /^#[0-9a-f]{3,8}$/i.test(v) || /^[a-z][a-z0-9+.-]*:\/\//i.test(v) || /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v) || /^-?\d+([.,]\d+)?$/.test(v);
}

function isConditionTemplate(name: string): boolean {
  return name.startsWith('SmartObjects.Conditions.');
}

function fallbackText(doc: DocumentLike, ctx: FormatContext, t: Template): string | undefined {
  const data = (doc.data ?? {}) as Record<string, Json>;
  const used = new Set<string>();
  for (const token of parseFormat(formatOf(t))) {
    if (token.kind === 'param') {
      const p = paramNamed(t, token.name);
      if (p) used.add(p.name);
    }
  }
  const params = t.effectiveParameters.filter((p) => !used.has(p.name));
  // 1. a localized string with text, then a plain string
  for (const p of params) {
    const v = data[p.name];
    if (p.type === 'localized_string' && typeof v === 'string' && v !== '') {
      const s = ctx.stringText?.(v)?.trim();
      if (s) return s;
    }
  }
  for (const p of params) {
    const v = data[p.name];
    if (p.type === 'string' && typeof v === 'string' && v.trim() !== '' && !looksLikeData(v.trim())) return v.trim();
  }
  // 2. a referenced document that has a meaningful label of its own: what the document is about (its offer, its store
  //    item) before its condition, which only says when it runs
  const depth = ctx.depth ?? 0;
  if (depth < MAX_DEPTH) {
    const targets = params
      .filter((p) => p.type === 'document' && typeof data[p.name] === 'string')
      .map((p) => ({ p, target: ctx.resolve?.(data[p.name] as string) }))
      .filter((x): x is { p: Parameter; target: DocumentLike } => !!x.target);
    const conditions = targets.filter((x) => isConditionTemplate(x.target.template));
    for (const { target } of [...targets.filter((x) => !isConditionTemplate(x.target.template)), ...conditions]) {
      const info = labelInfo(target, { ...ctx, depth: depth + 1 });
      if (info.meaningful && info.core) return info.core;
    }
  }
  // 3. a price naming a product or listing items, then a reward with items
  for (const which of ['SmartObjects.Price', 'SmartObjects.Reward']) {
    for (const p of params) {
      if (p.type !== 'object' || p.template !== which) continue;
      const v = data[p.name];
      if (!v || typeof v !== 'object' || Array.isArray(v)) continue;
      const o = v as { type?: number; product?: { productId?: string } | null; items?: Json[] };
      const has = which === 'SmartObjects.Reward' ? (o.items ?? []).length > 0 : Number(o.type ?? 0) === 0 ? !!o.product?.productId : Number(o.type) === 1 && (o.items ?? []).length > 0;
      if (!has) continue;
      const text = formatValue(p, v, ctx).trim();
      if (!EMPTY_WORDS.has(text)) return text;
    }
  }
  return undefined;
}

/** The one-line label: the parts joined, whitespace collapsed; when the format says nothing but the id, the fallback chain of `labelInfo`. */
export function labelText(doc: DocumentLike, ctx: FormatContext, template?: Template): string {
  return labelInfo(doc, ctx, template).text;
}

/** A search hit's label: the document label when the template is known, else the template's short name and the id. */
export function searchHitLabel(doc: { id: string; unnyId?: string | null; template: string; data: unknown }, ctx: FormatContext): string {
  if (ctx.templates?.has(doc.template)) return labelText(doc, ctx);
  const short = doc.template.split('.').pop() ?? doc.template;
  return doc.unnyId ? `${short} [${doc.unnyId}]` : `${short} ${shortId(doc)}`;
}

/** The format editor's token palette for a template. */
export function formatTokens(template: Pick<Template, 'effectiveParameters'>): { label: string; token: string }[] {
  return [
    { label: 'Template display name', token: '{template.displayName}' },
    { label: 'Entity id', token: '{entity.id}' },
    { label: 'Line break', token: '{\\n}' },
    ...template.effectiveParameters.map((p) => ({ label: p.displayName ?? p.name, token: `{${p.name}}` })),
  ];
}
